import { supabase } from './supabaseClient';
import { getCurrentCopy } from '../i18n/current';

/**
 * Serviço de integração com Stripe via Supabase Edge Functions
 */

const STRIPE_PUBLISHABLE_KEY: string | undefined = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

// Ciclos de cobrança válidos
const VALID_BILLING_CYCLES: string[] = ['monthly', 'yearly'];

/**
 * Lê o client_id do GA4 a partir do cookie `_ga` (formato GA1.1.<p1>.<p2>).
 * Síncrono de propósito: precisa estar pronto antes do redirect pro Stripe.
 * O webhook usa esse client_id no Measurement Protocol para juntar a compra
 * (server-side) à mesma sessão/usuário do GA4. Null se cookies não aceitos.
 */
function getGaClientId(): string | null {
  const match = document.cookie.match(/_ga=GA\d\.\d\.(\d+\.\d+)/);
  return match ? match[1] : null;
}

/** Consentimento de cookies (LGPD) salvo no banner. Propagado ao webhook. */
function getCookieConsent(): string {
  try {
    return localStorage.getItem('meugantt_cookie_consent') || 'unknown';
  } catch {
    return 'unknown';
  }
}

/**
 * Valida o formato do priceId do Stripe
 * @param {string} priceId - ID do preço
 * @throws {Error} Se o priceId for inválido
 */
function validatePriceId(priceId: string): void {
  if (!priceId || typeof priceId !== 'string') {
    throw new Error('priceId é obrigatório e deve ser uma string');
  }
  if (!priceId.startsWith('price_')) {
    throw new Error('priceId inválido: deve começar com "price_"');
  }
  // Validar comprimento mínimo (price_ + pelo menos alguns caracteres)
  if (priceId.length < 10) {
    throw new Error('priceId inválido: formato incorreto');
  }
}

/**
 * Valida o ciclo de cobrança
 * @param {string} billingCycle - Ciclo de cobrança
 * @throws {Error} Se o ciclo for inválido
 */
function validateBillingCycle(billingCycle: string): void {
  if (!VALID_BILLING_CYCLES.includes(billingCycle)) {
    throw new Error(`billingCycle inválido: deve ser ${VALID_BILLING_CYCLES.join(' ou ')}`);
  }
}

/**
 * Cria uma sessão de checkout no Stripe
 * @param {string} priceId - ID do preço no Stripe (price_xxx)
 * @param {string} billingCycle - 'monthly' ou 'yearly'
 * @returns {Promise<{url: string}>} - URL do checkout
 */
export async function createCheckoutSession(priceId: string, billingCycle: string = 'monthly', trialDays?: number): Promise<{ url: string }> {
  // Validar inputs antes de enviar para o servidor
  validatePriceId(priceId);
  validateBillingCycle(billingCycle);

  const { data: { session } } = await supabase.auth.getSession();

  if (!session) {
    throw new Error(getCurrentCopy().common.serviceErrors.userNotAuthenticated);
  }

  const body: Record<string, unknown> = {
    priceId,
    billingCycle,
    successUrl: `${window.location.origin}/?checkout=success`,
    cancelUrl: `${window.location.origin}/?checkout=canceled`,
    accessToken: session.access_token,
    // Para o purchase server-side (GA4 Measurement Protocol) no webhook:
    cookieConsent: getCookieConsent()
  };

  // client_id do GA4 (se houver) para juntar a compra à sessão do usuário.
  const gaClientId = getGaClientId();
  if (gaClientId) {
    body.gaClientId = gaClientId;
  }

  if (trialDays) {
    body.trialDays = trialDays;
  }

  const response = await supabase.functions.invoke('stripe-checkout', {
    body,
    headers: {
      Authorization: `Bearer ${session.access_token}`
    }
  });

  if (response.error) {
    throw new Error(response.error.message || 'Erro ao criar checkout');
  }

  return response.data as { url: string };
}

/**
 * Abre o portal do cliente Stripe para gerenciar assinatura
 * @returns {Promise<{url: string}>} - URL do portal
 */
export async function createPortalSession(): Promise<{ url: string }> {
  const { data: { session } } = await supabase.auth.getSession();

  if (!session) {
    throw new Error(getCurrentCopy().common.serviceErrors.userNotAuthenticated);
  }

  const response = await supabase.functions.invoke('stripe-portal', {
    body: {
      returnUrl: `${window.location.origin}/`,
      accessToken: session.access_token
    },
    headers: {
      Authorization: `Bearer ${session.access_token}`
    }
  });

  if (response.error) {
    throw new Error(response.error.message || 'Erro ao abrir portal');
  }

  return response.data as { url: string };
}

/**
 * Redireciona para o checkout do Stripe
 */
export async function redirectToCheckout(priceId: string, billingCycle: string = 'monthly', trialDays?: number): Promise<void> {
  try {
    const { url } = await createCheckoutSession(priceId, billingCycle, trialDays);
    window.location.href = url;
  } catch (error) {
    console.error('Erro ao redirecionar para checkout:', error);
    throw error;
  }
}

/**
 * Redireciona para o portal do cliente
 */
export async function redirectToPortal(): Promise<void> {
  try {
    const { url } = await createPortalSession();
    window.location.href = url;
  } catch (error) {
    console.error('Erro ao abrir portal:', error);
    throw error;
  }
}

/**
 * Verifica se Stripe está configurado
 */
export function isStripeConfigured(): boolean {
  return !!STRIPE_PUBLISHABLE_KEY;
}
